import { Component, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { PortalNotificationType } from '@app/core/interfaces/portal-notification';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { PortalTimeAgoPipe } from '@app/shared/pipes/portal-time-ago.pipe';
import { SharedModule } from '@app/shared/shared.module';
import { componentState } from '@app/utils/component-state';
import { watch } from '@app/utils/signals/watch';
import { sortBy } from '@app/utils/sort/sort-by';
import { currentLanguage } from '@app/utils/translate';
import { NotificationDialogComponent } from '@business/notification/components/notification-dialog/notification-dialog.component';
import { MyPortalNotificationViewModel } from '@business/notification/models/my-portal-notifications-view-model';
import { getTranslationsByKey } from '@business/notification/models/portal-notification-helpers';
import { PortalNotificationService } from '@business/notification/portal-notification.service';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'portal-maintenance-messages',
  imports: [NgbCollapse, SharedModule, PortalTimeAgoPipe],
  templateUrl: './maintenance-messages.component.html',
  styleUrls: ['./maintenance-messages.component.scss'],
})
export class MaintenanceMessagesComponent {
  language = currentLanguage();
  myNotifications = toSignal(this.portalNotificationService.myNotifications.data$);
  maintenance = computed(() => {
    const notifications = this.myNotifications()?.notifications ?? [];

    return notifications
      .filter(
        (notification) =>
          notification.type === PortalNotificationType.MAINTENANCE &&
          (notification.seen === false || notification.important)
      )
      .map((notification) => {
        return {
          ...notification,
          title: getTranslationsByKey(notification.titles, this.language()).title,
        };
      });
  });

  latestPublicationDate = computed<Date | null>(() => {
    if (this.maintenance().length === 0) {
      return null;
    }

    return this.maintenance().toSorted(sortBy([['updatedAt', 'desc']]))[0].updatedAt;
  });

  isCollapsed: boolean = true;
  state = componentState();

  constructor(
    private dialogService: DialogService,
    private portalNotificationService: PortalNotificationService
  ) {
    watch([this.maintenance], ([notifications]) => {
      document.body.classList.toggle('app--show-maintenance-messages', notifications.length > 0);
    });
  }

  markAsSeen(id: number): void {
    this.state.handle(this.portalNotificationService.markAsSeen(id), `mark-as-seen:${id}`).subscribe();
  }

  onBackdropClicked(event: Event): void {
    if (event.currentTarget === event.target) {
      this.isCollapsed = !this.isCollapsed;
    }
  }

  open(notification: MyPortalNotificationViewModel): void {
    this.dialogService.open(NotificationDialogComponent).withInput({
      notificationId: notification.id,
      translations: notification._original.translations,
      type: notification.type,
    });
  }
}
